import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Rating,
  IconButton,
  Badge,
} from '@mui/material';
import {
  Search,
  MoreVert,
  Edit,
  Delete,
  Visibility,
  Add,
  FilterList,
  LocationOn,
  Email,
  ViewList,
  ViewModule,
  VpnKey,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { colors } from '../theme/theme';
import StudioFormDialog from '../components/StudioFormDialog';
import AvatarWithPlaceholder from '../components/AvatarWithPlaceholder';
import { resolveStudioLogoUrl } from '../utils/studioLogo';
import AdvancedFiltersPopover, { AdvancedFilterSection } from '../components/AdvancedFiltersPopover';
import {
  EMPTY_STUDIO_ADVANCED_FILTERS,
  SortByFilterValue,
  SortDirFilterValue,
  StudioAdvancedFilters,
} from '../components/advancedFilterTypes';
import { isOwnerRole } from '../utils/roles';

interface Studio {
  _id: string;
  studioName: string;
  email: string;
  phoneNumber: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  images?: string[];
  logo?: string;
  description: string;
  isActive: boolean;
  premiumStudio: boolean;
  studioType: 'wellness' | 'workout' | 'event';
  isSubscribed: boolean;
  subscriptionEndDate?: string;
  rating: number;
  totalReviews?: number;
  workingHoursFrom?: string;
  workingHoursTo?: string;
  studioPermissions: {
    isAdmin: boolean;
    allowClassCreation: boolean;
    allowEventCreation: boolean;
  };
  deletionRequest?: 'DELETION_REQUESTED' | 'DELETION_ACCEPTED' | 'DELETION_REJECTED';
  adminPanelAccess?: boolean;
  /** Replaces isBranchParent + parentStudio: a studio belongs to a brand. */
  brand?: { _id: string; name: string } | null;
  branchName?: string | null;
  createdAt: string;
  updatedAt: string;
}

function parseSingleParam<T extends string>(raw: string | null, allowed: T[]): T | null {
  if (!raw) return null;
  const value = raw.split(',')[0];
  return allowed.includes(value as T) ? (value as T) : null;
}

const LEGACY_SORT_MAP: Record<string, { sortBy: SortByFilterValue; sortDir: SortDirFilterValue }> = {
  subscribers_desc: { sortBy: 'subscribers', sortDir: 'desc' },
  subscribers_asc: { sortBy: 'subscribers', sortDir: 'asc' },
  revenue_desc: { sortBy: 'revenue', sortDir: 'desc' },
  revenue_asc: { sortBy: 'revenue', sortDir: 'asc' },
};

function parseSortFromParams(params: URLSearchParams): Pick<StudioAdvancedFilters, 'sortBy' | 'sortDir'> {
  const sortBy = parseSingleParam(params.get('sortBy'), ['subscribers', 'revenue']);
  const sortDir = parseSingleParam(params.get('sortDir'), ['desc', 'asc']);

  if (sortBy || sortDir) {
    return { sortBy, sortDir: sortBy ? sortDir : null };
  }

  const legacySort = params.get('sort');
  if (legacySort) {
    const parsed = LEGACY_SORT_MAP[legacySort.split(',')[0]];
    if (parsed) return parsed;
  }

  return { sortBy: null, sortDir: null };
}

function parseFiltersFromParams(params: URLSearchParams): StudioAdvancedFilters {
  const { sortBy, sortDir } = parseSortFromParams(params);
  return {
    status: parseSingleParam(params.get('status'), ['active', 'inactive']),
    subscription: parseSingleParam(params.get('subscription'), ['subscribed', 'not_subscribed']),
    category: parseSingleParam(params.get('category'), ['workout', 'wellness']),
    sortBy,
    sortDir,
  };
}

function filtersToParams(filters: StudioAdvancedFilters, searchTerm: string): URLSearchParams {
  const params = new URLSearchParams();
  if (searchTerm) params.set('q', searchTerm);
  if (filters.status) params.set('status', filters.status);
  if (filters.subscription) params.set('subscription', filters.subscription);
  if (filters.category) params.set('category', filters.category);
  if (filters.sortBy) params.set('sortBy', filters.sortBy);
  if (filters.sortBy && filters.sortDir) params.set('sortDir', filters.sortDir);
  return params;
}

function countActiveFilters(filters: StudioAdvancedFilters): number {
  return (
    (filters.status ? 1 : 0) +
    (filters.subscription ? 1 : 0) +
    (filters.category ? 1 : 0) +
    (filters.sortBy && filters.sortDir ? 1 : 0)
  );
}

const StudiosPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { admin } = useAuth();
  const isStudio = isOwnerRole(admin?.role);
  const [studios, setStudios] = useState<Studio[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const searchTerm = searchParams.get('q') ?? '';
  const [searchInput, setSearchInput] = useState(searchTerm);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const skipSearchSyncRef = useRef(false);
  const appliedFilters = useMemo(() => parseFiltersFromParams(searchParams), [searchParams]);
  const activeFilterCount = countActiveFilters(appliedFilters);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [draftFilters, setDraftFilters] = useState<StudioAdvancedFilters>(EMPTY_STUDIO_ADVANCED_FILTERS);
  const [selectedStudio, setSelectedStudio] = useState<Studio | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [studioToDelete, setStudioToDelete] = useState<Studio | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [studioToEdit, setStudioToEdit] = useState<Studio | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const fetchStudios = useCallback(async () => {
    if (isStudio) {
      return; // Studios shouldn't fetch all studios
    }
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.getStudios();
      
      // Admin endpoint returns: { success: true, data: [...], total: number }
      console.log('Studio API Response:', response);
      
      const studiosData = response.data || [];
      console.log('Studios Data:', studiosData);
      
      setStudios(studiosData);
    } catch (err: any) {
      console.error('Error fetching studios:', err);
      setError(err.message || 'Failed to fetch studios');
    } finally {
      setIsLoading(false);
    }
  }, [isStudio]);

  useEffect(() => {
    if (isStudio) {
      navigate('/dashboard');
      return;
    }
    fetchStudios();
  }, [isStudio, navigate, fetchStudios]);

  useEffect(() => {
    if (skipSearchSyncRef.current) {
      skipSearchSyncRef.current = false;
      return;
    }
    setSearchInput(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      skipSearchSyncRef.current = true;
      setSearchParams(prev => filtersToParams(parseFiltersFromParams(prev), value));
    }, 400);
  };

  const openFilterPanel = (event: React.MouseEvent<HTMLElement>) => {
    setDraftFilters(appliedFilters);
    setFilterAnchorEl(event.currentTarget);
  };

  const closeFilterPanel = () => {
    setFilterAnchorEl(null);
  };

  const applyFilters = () => {
    skipSearchSyncRef.current = true;
    setSearchParams(filtersToParams(draftFilters, searchInput));
    closeFilterPanel();
  };

  const clearDraftFilters = () => {
    setDraftFilters(EMPTY_STUDIO_ADVANCED_FILTERS);
  };

  const clearAllFilters = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setSearchInput('');
    skipSearchSyncRef.current = true;
    setSearchParams(new URLSearchParams());
    setDraftFilters(EMPTY_STUDIO_ADVANCED_FILTERS);
    closeFilterPanel();
  };

  const studioFilterSections = useMemo((): AdvancedFilterSection[] => {
    const toggleSingle = <K extends 'status' | 'subscription' | 'category'>(key: K, value: string) => {
      setDraftFilters(prev => ({
        ...prev,
        [key]: prev[key] === value ? null : value,
      }));
    };

    return [
      {
        type: 'single-select-toggle',
        label: 'Status',
        options: [
          { value: 'active', label: 'Active' },
          { value: 'inactive', label: 'Inactive' },
        ],
        value: draftFilters.status,
        onToggle: value => toggleSingle('status', value),
      },
      {
        type: 'single-select-toggle',
        label: 'Subscription',
        options: [
          { value: 'subscribed', label: 'Subscribed' },
          { value: 'not_subscribed', label: 'Not Subscribed' },
        ],
        value: draftFilters.subscription,
        onToggle: value => toggleSingle('subscription', value),
      },
      {
        type: 'single-select-toggle',
        label: 'Category',
        options: [
          { value: 'workout', label: 'Workout' },
          { value: 'wellness', label: 'Wellness' },
        ],
        value: draftFilters.category,
        onToggle: value => toggleSingle('category', value),
      },
    ];
  }, [draftFilters]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, studio: Studio) => {
    event.stopPropagation(); // Prevent card click from firing
    setAnchorEl(event.currentTarget);
    setSelectedStudio(studio);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedStudio(null);
  };

  const handleViewStudio = () => {
    if (selectedStudio) {
      navigate(`/studios/${selectedStudio._id}`);
    }
    handleMenuClose();
  };

  const handleEditStudio = () => {
    if (selectedStudio) {
      setStudioToEdit(selectedStudio);
      setEditDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDeleteStudio = () => {
    if (selectedStudio) {
      setStudioToDelete(selectedStudio);
      setDeleteDialogOpen(true);
    }
    handleMenuClose();
  };

  const confirmDeleteStudio = async () => {
    if (studioToDelete) {
      try {
        setIsDeleting(true);
        setError(null);
        await apiService.deleteStudio(studioToDelete._id);
        setStudios(studios.filter(studio => studio._id !== studioToDelete._id));
        setDeleteDialogOpen(false);
        setStudioToDelete(null);
        // Show success message
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to delete studio');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleToggleAdminPanelAccess = async () => {
    if (selectedStudio) {
      try {
        setError(null);
        const newValue = !selectedStudio.adminPanelAccess;
        await apiService.toggleAdminPanelAccess(selectedStudio._id, newValue);
        setStudios(studios.map(studio => 
          studio._id === selectedStudio._id 
            ? { ...studio, adminPanelAccess: newValue }
            : studio
        ));
        handleMenuClose();
      } catch (err: any) {
        setError(err.message || 'Failed to toggle admin panel access');
      }
    }
  };

  const filteredStudios = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    let result = studios.filter(studio => {
      if (q) {
        const matchesSearch =
          studio.studioName?.toLowerCase().includes(q) ||
          studio.email?.toLowerCase().includes(q) ||
          studio.city?.toLowerCase().includes(q) ||
          studio.state?.toLowerCase().includes(q);
        if (!matchesSearch) return false;
      }

      if (appliedFilters.status === 'active' && !studio.isActive) return false;
      if (appliedFilters.status === 'inactive' && studio.isActive) return false;

      if (appliedFilters.subscription === 'subscribed' && !studio.isSubscribed) return false;
      if (appliedFilters.subscription === 'not_subscribed' && studio.isSubscribed) return false;

      if (appliedFilters.category === 'workout' && studio.studioType !== 'workout') return false;
      if (appliedFilters.category === 'wellness' && studio.studioType !== 'wellness') return false;

      return true;
    });

    // Alphabetical. (Revenue ranking was removed: computing every studio's
    // revenue on page load slowed the whole backend.)
    result = [...result].sort((a, b) =>
      (a.studioName || '').localeCompare(b.studioName || '', 'en', { sensitivity: 'base' }),
    );

    return result;
  }, [studios, searchTerm, appliedFilters]);

  const hasActiveFilters = activeFilterCount > 0;

  const getStudioTypeColor = (type: string) => {
    switch (type) {
      case 'wellness':
        return colors.success;
      case 'workout':
        return colors.error;
      case 'event':
        return colors.warning;
      default:
        return colors.textSecondary;
    }
  };


  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h3" component="h1" fontWeight="bold" color={colors.textPrimary} gutterBottom>
            Studios Management
          </Typography>
          <Typography variant="body1" color={colors.textSecondary}>
            Manage and monitor all partner studios in your platform
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          size="large"
          onClick={() => setCreateDialogOpen(true)}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            fontWeight: 600,
            textTransform: 'none'
          }}
        >
          Create Studio
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 4 }}>
        <Card sx={{ 
          borderLeft: `4px solid ${colors.primary}`,
          borderRadius: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <CardContent>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Total Studios
            </Typography>
            <Typography variant="h4" fontWeight="bold" color={colors.primary}>
              {studios.length.toLocaleString()}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{
          borderLeft: `4px solid ${colors.success}`,
          borderRadius: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <CardContent>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Active Studios
            </Typography>
            <Typography variant="h4" fontWeight="bold" color={colors.success}>
              {studios.filter(s => s.isActive).length.toLocaleString()}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ 
          borderLeft: `4px solid ${colors.info}`,
          borderRadius: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <CardContent>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Subscribed
            </Typography>
            <Typography variant="h4" fontWeight="bold" color={colors.info}>
              {studios.filter(s => s.isSubscribed).length.toLocaleString()}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ 
          borderLeft: `4px solid ${colors.warning}`,
          borderRadius: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <CardContent>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Premium
            </Typography>
            <Typography variant="h4" fontWeight="bold" color={colors.warning}>
              {studios.filter(s => s.premiumStudio).length.toLocaleString()}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Search and Filters */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <TextField
              fullWidth
              placeholder="Search studios by name, email, city, or state..."
              value={searchInput}
              onChange={handleSearch}
              variant="outlined"
              size="medium"
              sx={{ flex: 1, minWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: colors.textSecondary }} />
                  </InputAdornment>
                ),
                sx: {
                  fontSize: '1rem',
                  '& input': {
                    padding: '12px 14px',
                  }
                }
              }}
            />
            <Badge
              badgeContent={activeFilterCount}
              color="primary"
              invisible={activeFilterCount === 0}
              overlap="circular"
            >
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                size="large"
                onClick={openFilterPanel}
                sx={{
                  minWidth: 140,
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                  }
                }}
              >
                Filters
              </Button>
            </Badge>
            {/* View Toggle */}
            <Box 
              sx={{ 
                display: 'flex', 
                gap: 0.5, 
                backgroundColor: colors.backgroundTertiary,
                borderRadius: 2,
                p: 0.5
              }}
            >
              <IconButton
                onClick={() => setViewMode('grid')}
                sx={{
                  backgroundColor: viewMode === 'grid' ? colors.white : 'transparent',
                  color: viewMode === 'grid' ? colors.primary : colors.textSecondary,
                  boxShadow: viewMode === 'grid' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                  '&:hover': {
                    backgroundColor: viewMode === 'grid' ? colors.white : 'rgba(0,0,0,0.05)',
                  }
                }}
              >
                <ViewModule />
              </IconButton>
              <IconButton
                onClick={() => setViewMode('list')}
                sx={{
                  backgroundColor: viewMode === 'list' ? colors.white : 'transparent',
                  color: viewMode === 'list' ? colors.primary : colors.textSecondary,
                  boxShadow: viewMode === 'list' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                  '&:hover': {
                    backgroundColor: viewMode === 'list' ? colors.white : 'rgba(0,0,0,0.05)',
                  }
                }}
              >
                <ViewList />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <AdvancedFiltersPopover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={closeFilterPanel}
        sections={studioFilterSections}
        onApply={applyFilters}
        onClearAll={clearDraftFilters}
        width={340}
      />

      {/* Studios View */}
      {filteredStudios.length === 0 ? (
        <Card sx={{ borderRadius: 2, p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            {hasActiveFilters || searchTerm.trim() ? 'No studios match your filters' : 'No studios found'}
          </Typography>
          {(hasActiveFilters || searchTerm.trim()) && (
            <Button variant="contained" onClick={clearAllFilters} sx={{ mt: 2, textTransform: 'none' }}>
              Clear filters
            </Button>
          )}
        </Card>
      ) : viewMode === 'grid' ? (
        /* Grid View */
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 2.5,
          }}
        >
          {filteredStudios.map((studio) => (
            <Card
              key={studio._id}
              onClick={() => navigate(`/studios/${studio._id}`)}
              sx={{
                borderRadius: 2,
                height: '100%',
                border: `1px solid ${colors.divider}`,
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  borderColor: colors.primary,
                },
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box display="flex" alignItems="center" gap={1.5} flex={1}>
                    <AvatarWithPlaceholder
                      src={resolveStudioLogoUrl(studio)}
                      sx={{
                        width: 48,
                        height: 48,
                        backgroundColor: colors.primary,
                        color: colors.white,
                        fontWeight: 600,
                        fontSize: '1.1rem',
                      }}
                    >
                      {studio.studioName?.charAt(0)?.toUpperCase() || 'S'}
                    </AvatarWithPlaceholder>
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body1" fontWeight="600" sx={{ mb: 0.5 }}>
                        {studio.studioName || 'N/A'}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <LocationOn sx={{ fontSize: 13, color: colors.textSecondary }} />
                        <Typography variant="caption" color="textSecondary" sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {studio.city}, {studio.state}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, studio)}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{ color: colors.textSecondary }}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </Box>

                {/* Email */}
                <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                  <Email sx={{ fontSize: 14, color: colors.textSecondary }} />
                  <Typography variant="body2" color="textSecondary" sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1
                  }}>
                    {studio.email || 'N/A'}
                  </Typography>
                </Box>

                {/* Rating */}
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Rating value={studio.rating || 0} size="small" readOnly precision={0.5} />
                  <Typography variant="body2" fontWeight="600" color={colors.textPrimary}>
                    {studio.rating?.toFixed(1) || '0.0'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    ({studio.totalReviews || 0})
                  </Typography>
                </Box>

                {/* Chips */}
                <Box display="flex" gap={0.75} flexWrap="wrap" mb={2}>
                  <Chip
                    label={studio.isSubscribed ? 'Subscribed' : 'Not Subscribed'}
                    size="small"
                    sx={{
                      backgroundColor: studio.isSubscribed ? colors.success : colors.textSecondary,
                      color: colors.white,
                      fontWeight: 500,
                      fontSize: '0.7rem',
                      height: 24,
                    }}
                  />
                  {studio.studioType && (
                    <Chip
                      label={studio.studioType}
                      size="small"
                      sx={{
                        backgroundColor: getStudioTypeColor(studio.studioType),
                        color: colors.white,
                        textTransform: 'capitalize',
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        height: 24,
                      }}
                    />
                  )}
                  {/* One chip naming the business, instead of an HQ/Branch
                      badge that told you a position in a hierarchy. */}
                  {studio.brand && (
                    <Chip
                      label={studio.brand.name}
                      size="small"
                      sx={{ backgroundColor: '#764ba2', color: '#fff', fontWeight: 700, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>


                {/* Footer */}
                <Box display="flex" justifyContent="space-between" alignItems="center" pt={1.5} borderTop={`1px solid ${colors.divider}`}>
                  <Chip
                    label={studio.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    color={studio.isActive ? 'success' : 'error'}
                    sx={{ fontWeight: 500, fontSize: '0.7rem', height: 24 }}
                  />
                  <Typography variant="caption" color="textSecondary">
                    {new Date(studio.createdAt).toLocaleDateString('en-IN', { 
                      day: '2-digit', 
                      month: 'short',
                      year: 'numeric'
                    })}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        /* List View */
        <Card sx={{ borderRadius: 2 }}>
          <Box>
            {filteredStudios.map((studio, index) => (
              <Box
                key={studio._id}
                sx={{
                  borderBottom: index < filteredStudios.length - 1 ? `1px solid ${colors.divider}` : 'none',
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: colors.backgroundTertiary,
                  },
                }}
              >
                <Box 
                  sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
                  onClick={() => navigate(`/studios/${studio._id}`)}
                >
                  {/* Avatar */}
                  <AvatarWithPlaceholder
                    src={resolveStudioLogoUrl(studio)}
                    sx={{
                      width: 56,
                      height: 56,
                      backgroundColor: colors.primary,
                      color: colors.white,
                      fontWeight: 600,
                      fontSize: '1.3rem',
                    }}
                  >
                    {studio.studioName?.charAt(0)?.toUpperCase() || 'S'}
                  </AvatarWithPlaceholder>

                  {/* Studio Info */}
                  <Box flex={1} minWidth={0}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Typography variant="body1" fontWeight="600">
                        {studio.studioName || 'N/A'}
                      </Typography>
                      {studio.premiumStudio && (
                        <Chip label="Premium" size="small" sx={{ height: 20, fontSize: '0.65rem', backgroundColor: colors.warning, color: colors.white }} />
                      )}
                    </Box>
                    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <LocationOn sx={{ fontSize: 14, color: colors.textSecondary }} />
                        <Typography variant="body2" color="textSecondary">
                          {studio.city}, {studio.state}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Email sx={{ fontSize: 14, color: colors.textSecondary }} />
                        <Typography variant="body2" color="textSecondary">
                          {studio.email || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Rating */}
                  <Box display="flex" alignItems="center" gap={1} minWidth={120}>
                    <Rating value={studio.rating || 0} size="small" readOnly precision={0.5} />
                    <Typography variant="body2" fontWeight="600">
                      {studio.rating?.toFixed(1) || '0.0'}
                    </Typography>
                  </Box>

                  {/* Chips */}
                  <Box display="flex" gap={1} flexWrap="wrap" minWidth={200}>
                    <Chip
                      label={studio.isSubscribed ? 'Subscribed' : 'Not Subscribed'}
                      size="small"
                      sx={{
                        backgroundColor: studio.isSubscribed ? colors.success : colors.textSecondary,
                        color: colors.white,
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        height: 24,
                      }}
                    />
                    {studio.studioType && (
                      <Chip
                        label={studio.studioType}
                        size="small"
                        sx={{
                          backgroundColor: getStudioTypeColor(studio.studioType),
                          color: colors.white,
                          textTransform: 'capitalize',
                          fontWeight: 500,
                          fontSize: '0.7rem',
                          height: 24,
                        }}
                      />
                    )}
                    {/* One chip naming the business, instead of an HQ/Branch
                        badge that told you a position in a hierarchy. */}
                    {studio.brand && (
                      <Chip
                        label={studio.brand.name}
                        size="small"
                        sx={{ backgroundColor: '#764ba2', color: '#fff', fontWeight: 700, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>


                  {/* Status */}
                  <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5} minWidth={100}>
                    <Chip
                      label={studio.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={studio.isActive ? 'success' : 'error'}
                      sx={{ fontWeight: 500, fontSize: '0.7rem', height: 24 }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      {new Date(studio.createdAt).toLocaleDateString('en-IN', { 
                        day: '2-digit', 
                        month: 'short' 
                      })}
                    </Typography>
                  </Box>

                  {/* Actions */}
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, studio)}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{ color: colors.textSecondary }}
                  >
                    <MoreVert />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        </Card>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewStudio}>
          <Visibility sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleEditStudio}>
          <Edit sx={{ mr: 1 }} />
          Edit Studio
        </MenuItem>
        <MenuItem onClick={handleToggleAdminPanelAccess}>
          <VpnKey sx={{ mr: 1 }} />
          {selectedStudio?.adminPanelAccess ? 'Disable Admin Panel' : 'Enable Admin Panel'}
        </MenuItem>
        {/* Deleting a studio erases its whole history — super admin only. */}
        {admin?.role === 'super_admin' && (
          <MenuItem onClick={handleDeleteStudio} sx={{ color: colors.error }}>
            <Delete sx={{ mr: 1 }} />
            Delete Studio
          </MenuItem>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => !isDeleting && setDeleteDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown={isDeleting}
      >
        <DialogTitle sx={{ color: colors.error, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ fontSize: '1.5rem' }}>⚠️</Box>
          Delete Studio - Critical Action
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              ⚠️ WARNING: This will permanently delete ALL associated data:
            </Typography>
            <Typography component="ul" variant="body2" sx={{ pl: 2, mb: 0, mt: 1 }}>
              <li><strong>All Events</strong> created by this studio</li>
              <li><strong>All Classes</strong> associated with this studio</li>
              <li><strong>All Sessions</strong> and bookings</li>
              <li><strong>All Instructors</strong> linked to this studio</li>
              <li><strong>All Reviews</strong> and ratings</li>
              <li><strong>All Promo Codes</strong>, transactions, and payout history</li>
              <li><strong>The Studio Account</strong> and all its data</li>
            </Typography>
          </Alert>
          
          <Box sx={{ bgcolor: colors.error + '10', p: 2, borderRadius: 1, mb: 2 }}>
            <Typography variant="body1" fontWeight={700} color={colors.error} gutterBottom>
              Studio to be deleted:
            </Typography>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              "{studioToDelete?.studioName}"
            </Typography>
            {studioToDelete?.email && (
              <Typography variant="body2" color="text.secondary">
                Email: {studioToDelete.email}
              </Typography>
            )}
          </Box>

          <Typography variant="body1" fontWeight={600} gutterBottom>
            Are you absolutely certain you want to proceed?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action <strong>CANNOT BE UNDONE</strong>. All events, classes, and related data will be permanently removed from the system.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            variant="outlined"
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteStudio} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
            sx={{ fontWeight: 600 }}
          >
            {isDeleting ? 'Deleting...' : 'Yes, Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Studio Dialog */}
      <StudioFormDialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setStudioToEdit(null); }}
        onSuccess={() => { fetchStudios(); setEditDialogOpen(false); setStudioToEdit(null); }}
        studio={studioToEdit || undefined}
      />

      {/* Create Studio Dialog */}
      <StudioFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => { fetchStudios(); setCreateDialogOpen(false); }}
      />
    </Box>
  );
};

export default StudiosPage;
